import React, { useEffect, useMemo, useRef, useState } from 'react'

import { RadioListOption } from 'features/charts-v2/components/RadioListOption'
import { useAbsolutePopoverPlacement } from 'features/charts-v2/hooks/use-popover-placement'
import { type MetricType } from 'features/charts-v2/types'
import { useEscapeDismiss } from 'hooks/use-escape-dismiss'

interface MetricOption {
  field: MetricType
  alias: string
  enabled?: boolean
}

interface MetricSelectionPopoverProps {
  onMetricSelect: (metric: MetricType | MetricType[]) => void
  onClose: () => void
  selectedMetric?: MetricType
  metricOptions: MetricOption[]
  // For multi-select mode (used by MetricSelectHeader)
  getSelectedMetrics?: () => MetricType[]
  triggerElement?: HTMLElement | null
  // Ref to the trigger element to prevent closing when clicking it
  triggerRef?: React.RefObject<HTMLElement>
}

export const MetricSelectionPopover: React.FC<MetricSelectionPopoverProps> = ({
  onMetricSelect,
  onClose,
  selectedMetric,
  metricOptions,
  getSelectedMetrics,
  triggerElement,
  triggerRef,
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const highlightedItemRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const { isMeasured, style } = useAbsolutePopoverPlacement({ popoverRef })

  // Auto-focus the popover when it opens
  useEffect(() => {
    if (popoverRef.current) {
      popoverRef.current.focus()
    }
  }, [])

  useEscapeDismiss({ onClose })

  // Handle click outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      const isInsidePopover = popoverRef.current?.contains(target)
      const isInsideTrigger =
        triggerRef?.current?.contains(target) ||
        triggerElement?.contains(target)

      if (!isInsidePopover && !isInsideTrigger) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose, triggerRef, triggerElement])

  // Create a flat list of navigable items for keyboard navigation (only enabled options)
  const navigableItems = useMemo(() => {
    // In multi-select mode, also filter out already-selected metrics (except when editing a card)
    if (getSelectedMetrics) {
      const currentSelection = getSelectedMetrics()
      return metricOptions.filter(
        (option) =>
          option.enabled !== false &&
          (!currentSelection.includes(option.field) ||
            option.field === selectedMetric),
      )
    }
    return metricOptions.filter((option) => option.enabled !== false)
  }, [metricOptions, getSelectedMetrics, selectedMetric])

  // Reset highlighted index when navigable items change - start at 0 for immediate navigation
  useEffect(() => {
    setHighlightedIndex(navigableItems.length > 0 ? 0 : -1)
  }, [navigableItems.length])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedItemRef.current && highlightedIndex >= 0) {
      highlightedItemRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }, [highlightedIndex])

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (navigableItems.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setHighlightedIndex((prev) =>
          prev < navigableItems.length - 1 ? prev + 1 : prev,
        )
        break
      case 'ArrowUp':
        event.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        event.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < navigableItems.length) {
          const item = navigableItems[highlightedIndex]
          if (item) handleSelectMetric(item.field)
        }
        break
      // Escape is handled by useEscapeDismiss above — it needs to work
      // regardless of whether this popover holds DOM focus, so it isn't
      // duplicated here.
    }
  }

  // Helper to get the index of an option in the navigable items list
  const getNavigableIndexForOption = (option: MetricOption): number => {
    return navigableItems.findIndex((item) => item.field === option.field)
  }

  const handleSelectMetric = (metric: MetricType): void => {
    // Multi-select mode for MetricSelectHeader
    if (getSelectedMetrics) {
      const currentSelection = getSelectedMetrics()
      const isCurrentlySelected = currentSelection.includes(metric)

      if (isCurrentlySelected) {
        // Deselect: remove from array
        const newSelection = currentSelection.filter((m) => m !== metric)
        // Keep at least one metric selected
        if (newSelection.length > 0) {
          onMetricSelect(newSelection)
        }
      } else {
        // Select: add to array
        onMetricSelect([...currentSelection, metric])
      }
    } else {
      // Single select mode
      onMetricSelect(metric)
      onClose()
    }
  }

  const isMetricSelected = (metric: MetricType): boolean => {
    if (getSelectedMetrics) {
      return getSelectedMetrics().includes(metric)
    }
    return selectedMetric === metric
  }

  return (
    <div
      ref={popoverRef}
      data-yogi-overlay="true"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      style={{
        position: 'absolute',
        left: '0',
        right: '0',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        maxHeight: 400,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
        visibility: isMeasured ? 'visible' : 'hidden',
        ...style,
      }}
    >
      {/* Options List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px',
          minHeight: 0,
        }}
      >
        {metricOptions.length > 0 && (
          <>
            {metricOptions.map((option) => {
              const isSelected = isMetricSelected(option.field)
              // In multi-select mode, disable already-selected metrics
              // In single-select mode (edit card), disable other selected metrics but keep current one enabled
              const isDisabled =
                option.enabled === false ||
                (getSelectedMetrics !== undefined &&
                  isSelected &&
                  option.field !== selectedMetric)
              const navIndex = getNavigableIndexForOption(option)
              const isHighlighted = navIndex === highlightedIndex

              return (
                <RadioListOption
                  key={option.field}
                  ref={isHighlighted ? highlightedItemRef : null}
                  label={option.alias || option.field}
                  isSelected={isSelected}
                  isHighlighted={isHighlighted}
                  isDisabled={isDisabled}
                  onClick={() => {
                    if (isDisabled) return
                    handleSelectMetric(option.field)
                  }}
                />
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}
